// =====================================================
// Lever Back Hooks – Upgrade Logic (UNBUNDLED, FULL)
// Proactive Commerce / Dawn-style cart drawer
//
// Includes:
// 1) UI button/checkbox sync (Classic button + Premium label)
// 2) Add-to-cart intercept: adds main + lever as 2 separate products
// 3) Main product shows: "Upgrade: Upgraded lever"
// 4) Lever product shows TWO property lines:
//      - "Attached to: {product name}"
//      - "Upgrade note: Required..."
//    + Adds classes in cart drawer so you can style each line:
//      - .product-option.is-attached-to
//      - .product-option.is-upgrade-note
// 5) Cart drawer refresh safety + "is-empty" class fix
// 6) Hide Shop Pay / dynamic checkout when upgrade selected
// =====================================================

(function () {
  // ---------------------------
  // Config
  // ---------------------------
  const HOOK_FIELDSET_ID = "HookUpgrade";
  const UPGRADE_INPUT_ID = "lever-hooks-upgrade";

  // Lever properties (visible labels)
  const LEVER_PROP_ATTACHED_TO = "Upgrade on";
  const LEVER_PROP_NOTE = "Upgrade note";

  // Main product visible label
  const MAIN_PROP_UPGRADE_LABEL = "Upgrade";
  const MAIN_PROP_UPGRADE_VALUE = "Premium hook";

  // ---------------------------
  // Helpers (forms)
  // ---------------------------
  function getFormValue(form, key) {
    try {
      return new FormData(form).get(key);
    } catch (e) {
      return null;
    }
  }

  function getQty(form) {
    const fdQty = getFormValue(form, "quantity");
    const q1 = parseInt(fdQty, 10);
    if (Number.isFinite(q1) && q1 > 0) return q1;

    const qtyInput =
      form.querySelector('input[name="quantity"]') ||
      form.querySelector("quantity-input input") ||
      form.querySelector("[data-quantity-input]");

    const q2 = qtyInput ? parseInt(qtyInput.value, 10) : NaN;
    if (Number.isFinite(q2) && q2 > 0) return q2;

    return 1;
  }

  function getMainVariantId(form) {
    const fdId = getFormValue(form, "id");
    const i1 = parseInt(fdId, 10);
    if (Number.isFinite(i1)) return i1;

    const idInput = form.querySelector('input[name="id"]');
    const i2 = idInput ? parseInt(idInput.value, 10) : NaN;
    return Number.isFinite(i2) ? i2 : null;
  }

  // ---------------------------
  // Helpers (cart drawer)
  // ---------------------------
  function getCartDrawer() {
    return document.querySelector("cart-drawer");
  }

  function getSectionIds(cartDrawer) {
    return cartDrawer?.getSectionsToRender
      ? cartDrawer.getSectionsToRender().map((s) => s.id)
      : ["cart-drawer", "cart-icon-bubble"];
  }

  function forceCartDrawerNonEmptyState() {
    const drawerEl = getCartDrawer();
    if (!drawerEl) return;

    drawerEl.classList.remove("is-empty");
    document.getElementById("CartDrawer")?.classList.remove("is-empty");
    drawerEl.querySelector(".drawer__inner")?.classList.remove("is-empty");
  }

  // Add classes to lever note property rows so you can style them
  function enhanceLeverNoteClasses() {
    document.querySelectorAll(".cart-item .product-option").forEach((row) => {
      const dt = row.querySelector("dt");
      if (!dt) return;

      const label = (dt.textContent || "").replace(/\s+/g, " ").trim();

      if (label === `${LEVER_PROP_ATTACHED_TO}:`) row.classList.add("is-attached-to");
      if (label === `${LEVER_PROP_NOTE}:`) row.classList.add("is-upgrade-note");
    });
  }

  function afterDrawerRender() {
    forceCartDrawerNonEmptyState();
    enhanceLeverNoteClasses();
    installCartDrawerWatcher(); // safe to call repeatedly
  }

  // Watch cart drawer for re-renders and re-apply classes
  function installCartDrawerWatcher() {
    const container =
      document.querySelector("#CartDrawer-CartItems") ||
      document.querySelector("cart-drawer-items") ||
      document.querySelector("cart-drawer");

    if (!container) return;

    if (container.dataset.leverWatcherInstalled === "1") return;
    container.dataset.leverWatcherInstalled = "1";

    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        enhanceLeverNoteClasses();
      });
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  async function addItemsAndOpenDrawer(items) {
    const cartDrawer = getCartDrawer();
    if (!cartDrawer) return;

    const sectionIds = getSectionIds(cartDrawer);
    const formData = new FormData();

    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, String(item.id));
      formData.append(`items[${index}][quantity]`, String(item.quantity));

      if (item.properties) {
        Object.entries(item.properties).forEach(([k, v]) => {
          if (v == null || String(v).trim() === "") return;
          formData.append(`items[${index}][properties][${k}]`, String(v));
        });
      }
    });

    formData.append("sections", sectionIds.join(","));
    formData.append("sections_url", "/cart");

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: formData,
      cache: "no-store",
    });

    if (!res.ok) {
      let msg = "";
      try {
        const err = await res.json();
        msg = err?.description || err?.message || JSON.stringify(err);
      } catch (e) {}
      throw new Error(msg || "Cart add failed");
    }

    const state = await res.json();

    if (typeof cartDrawer.renderContents === "function") {
      cartDrawer.renderContents(state);
      setTimeout(afterDrawerRender, 0);
      setTimeout(afterDrawerRender, 50);
      setTimeout(afterDrawerRender, 150);
    } else {
      document.querySelector("#cart-icon-bubble")?.click();
      setTimeout(afterDrawerRender, 250);
    }
  }

  // ---------------------------
  // UI: Classic button + Premium checkbox sync
  // ---------------------------
  function getHookRoot() {
    return document.getElementById(HOOK_FIELDSET_ID);
  }

  function getHookEls() {
    const root = getHookRoot();
    if (!root) return {};
    return {
      root,
      defaultBtn: root.querySelector('[data-hook-choice="default"]'),
      upgradeLabel: root.querySelector('[data-hook-choice="upgrade"]'),
      upgradeInput: root.querySelector(`#${UPGRADE_INPUT_ID}`),
    };
  }

  function syncHookUI() {
    const { defaultBtn, upgradeLabel, upgradeInput } = getHookEls();
    if (!defaultBtn || !upgradeLabel || !upgradeInput) return;

    const isOn = !!upgradeInput.checked;
    upgradeLabel.classList.toggle("selected", isOn);
    defaultBtn.classList.toggle("selected", !isOn);
    defaultBtn.setAttribute("aria-pressed", (!isOn).toString());
  }

  document.addEventListener("click", (e) => {
    const root = e.target.closest(`#${HOOK_FIELDSET_ID}`);
    if (!root) return;

    const { defaultBtn, upgradeLabel, upgradeInput } = getHookEls();
    if (!defaultBtn || !upgradeLabel || !upgradeInput) return;

    if (e.target.closest('[data-hook-choice="default"]')) {
      upgradeInput.checked = false;
      upgradeInput.dispatchEvent(new Event("change", { bubbles: true }));
      syncHookUI();
      return;
    }

    if (e.target.closest('[data-hook-choice="upgrade"]')) {
      setTimeout(syncHookUI, 0);
    }
  });

  document.addEventListener("change", (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.id !== UPGRADE_INPUT_ID) return;

    syncHookUI();
    toggleDynamicCheckout();
  });

  // ---------------------------
  // Hide Shop Pay / dynamic checkout when upgrade selected
  // ---------------------------
  function toggleDynamicCheckout() {
    const upgrade = document.getElementById(UPGRADE_INPUT_ID);
    const dynamicWrapper =
      document.querySelector(".shopify-payment-button") ||
      document.querySelector('[data-shopify="payment-button"]');

    if (!upgrade || !dynamicWrapper) return;
    dynamicWrapper.style.display = upgrade.checked ? "none" : "";
  }

  document.addEventListener("DOMContentLoaded", () => {
    syncHookUI();
    toggleDynamicCheckout();
    enhanceLeverNoteClasses();
    installCartDrawerWatcher();
  });

  // ---------------------------
  // Add-to-cart intercept (UNBUNDLED)
  // ---------------------------
  document.addEventListener(
    "submit",
    async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || !form.action.includes("/cart/add")) return;

      const upgrade = document.getElementById(UPGRADE_INPUT_ID);
      if (!upgrade || !upgrade.checked) return;

      const upgradeVariantId = parseInt(upgrade.dataset.upgradeVariantId, 10);
      if (!Number.isFinite(upgradeVariantId)) return;

      const mainVariantId = getMainVariantId(form);
      if (!mainVariantId) return;

      const qty = getQty(form);
      const productName = (upgrade.dataset.mainTitle || "This product").trim();

      e.preventDefault();
      e.stopImmediatePropagation();

      try {
        await addItemsAndOpenDrawer([
          {
            id: mainVariantId,
            quantity: qty,
            properties: {
              [MAIN_PROP_UPGRADE_LABEL]: MAIN_PROP_UPGRADE_VALUE,
            },
          },
          {
            id: upgradeVariantId,
            quantity: qty,
            properties: {
              [LEVER_PROP_ATTACHED_TO]: productName,
              [LEVER_PROP_NOTE]:
                "Required for the premium hook. If removed, your item will not be upgraded.",
            },
          },
        ]);
      } catch (err) {
        console.warn("[Lever upgrade] error:", err);
        form.submit();
      }
    },
    true
  );
})();
